import * as zod from 'zod';

import type {
  DeviceCommand,
  DeviceConfiguration,
  EquipmentItem,
} from '@sb/integrations/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { getDistanceUnitInfo } from '@sb/remote-control/util/distance';
import { Expression } from '@sb/routine-runner';

import { getEwellixConfigurationsSync } from '../../EwellixLiftTLT/implementation/ewellixConfigLoader';

export namespace MoveVerticalLiftStepDatabase {
  export const name = 'Move vertical lift';
  export const description = 'Move vertical lift to position';
  export const deviceKinds = ['EwellixLiftTLT'] as const;
  export const librarySection = Step.LibrarySection.Basic;
  export const librarySort = '3';
  export const argumentKind = 'MoveVerticalLift';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    targetHeight: zod.union([zod.number(), Expression]).optional(),
    /**
     * Optional scale factor to convert the user's distance unit to meters at runtime.
     * Example: millimeter -> 0.001
     */
    unitScaleToMeters: zod.number().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export function getVerticalLiftInfo(equipment: EquipmentItem[]): {
    config: DeviceConfiguration;
    minHeight: number;
    maxHeight: number;
  } | null {
    for (const device of equipment) {
      switch (device.config.kind) {
        case 'EwellixLiftTLT': {
          const { strokeVariant } = device.config;
          const strokeConfigurations = getEwellixConfigurationsSync();

          const ewellixConfiguration = strokeConfigurations[strokeVariant];

          return {
            config: device.config,
            minHeight: ewellixConfiguration.baseHeightMeters,
            maxHeight:
              ewellixConfiguration.baseHeightMeters +
              ewellixConfiguration.strokeMeters,
          };
        }

        default:
          continue;
      }
    }

    return null;
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const { targetHeight } = args;

    const getDeviceCommand = (): DeviceCommand => {
      if (targetHeight === undefined) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          fieldId: 'targetHeight',
          message: 'Target height is not configured',
        });
      }

      // If targetHeight is an expression, we cannot evaluate here. Defer to runner.
      if (typeof targetHeight !== 'number') {
        // Returning a dummy command will be incorrect; instead, we signal the caller to pass the expression through.
        // This branch should not be called; handled below by returning targetHeight directly in args.
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          fieldId: 'targetHeight',
          message: 'Target height expression must be evaluated at runtime',
        });
      }

      const dynamicBaseInfo = getVerticalLiftInfo(equipment);

      if (dynamicBaseInfo) {
        if (targetHeight < dynamicBaseInfo.minHeight) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            fieldId: 'targetHeight',
            message: 'Target height below minimum',
          });
        }

        if (targetHeight > dynamicBaseInfo?.maxHeight) {
          throw new ActionRequiredError({
            kind: 'invalidConfiguration',
            fieldId: 'targetHeight',
            message: 'Target height above maximum',
          });
        }
      }

      switch (dynamicBaseInfo?.config.kind) {
        case 'EwellixLiftTLT': {
          return {
            kind: 'EwellixLiftTLTCommand',
            heightMeters: targetHeight,
            speedPercentage: 1,
          };
        }
        default:
          throw new ActionRequiredError({
            kind: 'missingEquipment',
            message: 'No compatible equipment configured',
          });
      }
    };

    return {
      ...stepData,
      stepKind: 'MoveVerticalLift',
      args:
        targetHeight != null && typeof targetHeight !== 'number'
          ? // Defer evaluation to runner when expression is provided
            {
              targetHeight,
              unitScaleToMeters: args.unitScaleToMeters,
            }
          : { deviceCommand: getDeviceCommand() },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    distanceUnitInfo = getDistanceUnitInfo('millimeter'),
    includeStepName,
  }) => {
    if (
      args?.argumentKind !== argumentKind ||
      args.targetHeight === undefined ||
      typeof args.targetHeight !== 'number'
    ) {
      return null;
    }

    const target = distanceUnitInfo.toString(args.targetHeight);

    return `${includeStepName ? 'Move vertical lift ' : ''}to ${target}`;
  };
}

MoveVerticalLiftStepDatabase satisfies Step.StepKindInfo;
